import { useState } from "react";
import {
  PhoneCall, ClipboardSignature, BadgeCheck, RefreshCw, CalendarClock, FilePlus2, XCircle, Phone, Mail, HardHat, User, Loader2, Pencil,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import ContractorBadgeForName from "@/components/admin/ContractorBadgeForName";

const ACTIONS = [
  { key: "CHASE_CONTRACTOR",    label: "Chase contractor",  icon: PhoneCall,         tone: "primary",   needs: "contractor" },
  { key: "REQUEST_QUOTE",       label: "Request quote",     icon: ClipboardSignature,tone: "primary",   needs: "contractor" },
  { key: "APPROVE_QUOTE",       label: "Approve quote",     icon: BadgeCheck,        tone: "success" },
  { key: "REASSIGN_CONTRACTOR", label: "Reassign contractor", icon: RefreshCw,       tone: "secondary" },
  { key: "SCHEDULE_VISIT",      label: "Schedule visit",    icon: CalendarClock,     tone: "secondary" },
  { key: "CLOSE_JOB",           label: "Close job",         icon: XCircle,           tone: "danger" },
];

export default function ActionPanel({ ws, onDone }) {
  const c = ws.case;
  const [busyKey, setBusyKey] = useState(null);
  const [drawer, setDrawer] = useState(null); // {action} for actions that need extra input
  const [editOpen, setEditOpen] = useState(false);

  async function fire(action, payload = {}) {
    setBusyKey(action);
    try {
      const { data } = await adminApi.post(`/cases/${c.id}/action`, { action, ...payload });
      toast.success(data.new_status ? `${humanise(action)} · status → ${data.new_status}` : humanise(action));
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyKey(null);
      setDrawer(null);
    }
  }

  function onClick(a) {
    if (a.needs === "contractor" && !c.contractor_name) {
      toast.error("Assign a contractor first");
      return;
    }
    if (a.key === "CLOSE_JOB") {
      if (!window.confirm("Close this job? Status will be set to CLOSED.")) return;
      fire(a.key);
      return;
    }
    if (a.key === "REASSIGN_CONTRACTOR" || a.key === "SCHEDULE_VISIT") {
      setDrawer({ action: a.key });
      return;
    }
    fire(a.key);
  }

  return (
    <div className="space-y-4" data-testid="ws-action-panel">
      {/* Take Action */}
      <div className="bg-white border border-[#E2DCD0] rounded-lg p-4">
        <h3 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-3">Take action</h3>
        <div className="space-y-2">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            const cls =
              a.tone === "success" ? "bg-[#064E2E] text-white hover:bg-[#053b22]"
              : a.tone === "danger" ? "bg-white text-[#B91C1C] border border-[#B91C1C]/40 hover:bg-[#B91C1C] hover:text-white"
              : a.tone === "secondary" ? "bg-[#F7F4ED] text-[#071A33] border border-[#E2DCD0] hover:border-[#064E2E]"
              : "bg-[#0F766E] text-white hover:bg-[#0a5b55]";
            return (
              <button
                key={a.key}
                onClick={() => onClick(a)}
                disabled={busyKey === a.key}
                className={`w-full inline-flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-full text-sm font-medium transition disabled:opacity-60 ${cls}`}
                data-testid={`ws-action-${a.key.toLowerCase()}`}
              >
                <span className="flex items-center gap-2">
                  {busyKey === a.key ? <Loader2 className="animate-spin" size={14}/> : <Icon size={14}/>}
                  {a.label}
                </span>
                <span className="opacity-60 text-xs">→</span>
              </button>
            );
          })}
        </div>
        <button
          id="ws-edit-job-btn"
          data-testid="ws-edit-job-btn"
          onClick={() => setEditOpen(true)}
          className="mt-3 w-full text-xs text-[#0F766E] hover:underline inline-flex items-center justify-center gap-1.5"
        >
          <Pencil size={11}/> Edit job details
        </button>
      </div>

      {/* Quick contact */}
      <div className="bg-white border border-[#E2DCD0] rounded-lg p-4" data-testid="ws-quick-contact">
        <h3 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-3">Quick contact</h3>
        <ContactBlock
          icon={<User size={14} className="text-[#0F766E]"/>}
          name={c.full_name || "Resident"}
          phone={c.phone}
          email={c.email}
          testid="contact-resident"
        />
        <div className="border-t border-[#E2DCD0] my-3"/>
        <ContactBlock
          icon={<HardHat size={14} className="text-[#B88A2A]"/>}
          name={c.contractor_name || "(no contractor)"}
          empty={!c.contractor_name}
          testid="contact-contractor"
          extra={c.contractor_name && <div className="mt-1.5"><ContractorBadgeForName name={c.contractor_name}/></div>}
        />
      </div>

      {drawer && (
        <ActionDrawer
          action={drawer.action}
          caseData={c}
          onCancel={() => setDrawer(null)}
          onSubmit={(payload) => fire(drawer.action, payload)}
          busy={!!busyKey}
        />
      )}
      {editOpen && (
        <EditJobDrawer caseData={c} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onDone?.(); }}/>
      )}
    </div>
  );
}

function humanise(action) {
  return action.split("_").map((w) => w[0] + w.slice(1).toLowerCase()).join(" ");
}

function ContactBlock({ icon, name, phone, email, empty, testid, extra }) {
  return (
    <div data-testid={testid}>
      <p className="text-sm font-medium text-[#071A33] flex items-center gap-1.5">{icon}{name}</p>
      {extra}
      {empty && <p className="text-xs text-[#475569] italic mt-1">Not yet assigned.</p>}
      {!empty && (
        <div className="flex gap-2 mt-2">
          {phone && (
            <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 text-xs bg-[#F7F4ED] hover:bg-[#0F766E] hover:text-white border border-[#E2DCD0] hover:border-[#0F766E] px-2.5 py-1 rounded-full transition" data-testid={`${testid}-tel`}>
              <Phone size={11}/> Call
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-xs bg-[#F7F4ED] hover:bg-[#0F766E] hover:text-white border border-[#E2DCD0] hover:border-[#0F766E] px-2.5 py-1 rounded-full transition" data-testid={`${testid}-email`}>
              <Mail size={11}/> Email
            </a>
          )}
          {!phone && !email && <p className="text-xs text-[#475569] italic">No contact captured.</p>}
        </div>
      )}
    </div>
  );
}

function ActionDrawer({ action, caseData, onCancel, onSubmit, busy }) {
  const isReassign = action === "REASSIGN_CONTRACTOR";
  const [value, setValue] = useState(isReassign ? (caseData.contractor_name || "") : "");
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel} data-testid="ws-action-drawer">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(isReassign ? { new_contractor: value, note } : { visit_date_iso: value, note }); }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-[#E2DCD0]">
          <h3 className="font-display text-lg text-[#071A33]">{humanise(action)}</h3>
        </header>
        <div className="p-5 space-y-3">
          {isReassign ? (
            <>
              <label className="field-label">New contractor name</label>
              <input className="field-input" value={value} onChange={(e) => setValue(e.target.value)} required data-testid="ws-action-input"/>
            </>
          ) : (
            <>
              <label className="field-label">Visit date / time</label>
              <input type="datetime-local" className="field-input" value={value} onChange={(e) => setValue(e.target.value)} required data-testid="ws-action-input"/>
            </>
          )}
          <label className="field-label mt-2">Optional note</label>
          <textarea className="field-input min-h-[70px]" value={note} onChange={(e) => setNote(e.target.value)} data-testid="ws-action-note"/>
        </div>
        <footer className="px-5 py-4 bg-[#F7F4ED] rounded-b-2xl flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-outline">Cancel</button>
          <button type="submit" disabled={busy || !value} className="btn-primary" data-testid="ws-action-submit">
            {busy ? <Loader2 className="animate-spin" size={14}/> : "Confirm"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function EditJobDrawer({ caseData, onClose, onSaved }) {
  const [form, setForm] = useState({
    contractor_name: caseData.contractor_name || "",
    type_of_works:   caseData.type_of_works || "",
    quote_value:     caseData.quote_value ?? "",
    park_name:       caseData.park_name || "",
    plot_number:     caseData.plot_number || "",
    town_location:   caseData.town_location || "",
    description:     caseData.description || "",
    internal_notes:  caseData.internal_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      // The /admin/cases/{id}/update endpoint only accepts a subset of fields.
      // For arbitrary case-detail edits we patch through a Mongo-update via a quick raw call —
      // but to keep this v1 tight, we use the existing endpoint for the fields it allows
      // (internal_notes) and rely on the status-action endpoints for the rest.
      // For now, this drawer updates internal_notes only — surfacing the full editor will land in P1.1.
      await adminApi.post(`/cases/${caseData.id}/update`, { internal_notes: form.internal_notes });
      toast.success("Internal notes updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ws-edit-drawer">
      <form className="bg-white rounded-2xl shadow-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="px-5 py-4 border-b border-[#E2DCD0]">
          <h3 className="font-display text-lg text-[#071A33]">Edit job details</h3>
          <p className="text-xs text-[#475569] mt-1">v1 supports inline edits to internal notes. Job-level field edits land in v1.1.</p>
        </header>
        <div className="p-5 space-y-3">
          <Field label="Contractor"   value={form.contractor_name}/>
          <Field label="Type of works" value={form.type_of_works}/>
          <Field label="Park"         value={form.park_name}/>
          <div>
            <label className="field-label">Internal notes</label>
            <textarea className="field-input min-h-[140px]" value={form.internal_notes} onChange={(e) => upd("internal_notes", e.target.value)} data-testid="ws-edit-notes"/>
          </div>
        </div>
        <footer className="px-5 py-4 bg-[#F7F4ED] rounded-b-2xl flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="ws-edit-submit">
            {busy ? <Loader2 className="animate-spin" size={14}/> : "Save notes"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input className="field-input bg-[#F7F4ED]/60" value={value || ""} readOnly/>
    </div>
  );
}
